"use client";

import { isValidBookingPhone } from "@v1/backend/logic/scheduling/validate_phone";
import { cn } from "@v1/ui/utils";
import type { CountryCode } from "libphonenumber-js";
import { ChevronDown, Search, X } from "lucide-react";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactElement } from "react";
import {
  PUBLIC_BOOKING_LIGHT_TOKENS_CLASS,
  type PublicBookingFloatingPanelPos,
  computePublicBookingFloatingPanelPos,
} from "./public-booking-floating-panel";
import {
  BOOKING_PHONE_COUNTRIES,
  countryName,
  detectCountryFromPhoneValue,
  flagEmoji,
  getCountryCallingCode,
} from "./public-booking-phone-countries";
import "./public-booking-light-scope.css";
import { useTranslations } from "next-intl";
import { postBookingEmbedHeight } from "./public-booking-embed-height";
import { PublicBookingFloatingPanelPortal } from "./public-booking-floating-panel-portal";
import {
  formatPublicBookingPhoneValue,
  getPublicBookingPhoneDisplayValue,
  isEmptyPublicBookingPhoneValue,
} from "./public-booking-phone-value";
import { BOOKING_FOCUS_RING } from "./public-booking-ui";
import { useNotifyBookingEmbedHeightWhenOpen } from "./use-notify-booking-embed-height";

type PanelPos = PublicBookingFloatingPanelPos;

const PANEL_DESIRED_HEIGHT = 320;

/**
 * Phone input with a country-flag picker. The dropdown is rendered in a portal on
 * `document.body` with fixed positioning, so it is never clipped by the booking
 * card's `overflow-hidden` or the embed iframe. It flips above the field when there
 * is more room there (e.g. the field sits low in a short iframe), and caps its own
 * height to the available space, scrolling internally. Country auto-detects from a
 * typed `+XX` prefix; validity is reported inline via libphonenumber-js.
 */
export function PublicBookingPhoneInput({
  value,
  onChange,
  required = false,
  onValidChange,
  forceShowError = false,
  className,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly required?: boolean;
  readonly onValidChange?: (valid: boolean) => void;
  readonly forceShowError?: boolean;
  readonly className?: string;
}): ReactElement {
  const tI18n = useTranslations("public.bookingPhoneInput");
  const [country, setCountry] = useState<CountryCode>("DE");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [touched, setTouched] = useState(false);
  const [pos, setPos] = useState<PanelPos | null>(null);

  const fieldRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the flag in sync with what the user types.
  useEffect(() => {
    const detected = detectCountryFromPhoneValue(value);
    if (detected) setCountry(detected);
  }, [value]);

  const computePos = useCallback((): void => {
    const el = fieldRef.current;
    if (!el || typeof window === "undefined") return;
    setPos(computePublicBookingFloatingPanelPos(el, PANEL_DESIRED_HEIGHT));
  }, []);

  useNotifyBookingEmbedHeightWhenOpen(open);

  // Open: position, focus search. Close: reset search.
  useEffect(() => {
    if (!open) {
      setSearch("");
      return;
    }
    computePos();
    const t = setTimeout(() => searchRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open, computePos]);

  // Reposition on scroll (any ancestor) / resize while open.
  useEffect(() => {
    if (!open) return;
    const handler = (): void => {
      computePos();
      postBookingEmbedHeight();
    };
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open, computePos]);

  // Close on outside click (field OR portaled panel).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (fieldRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const dialCode = `+${getCountryCallingCode(country)}`;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return BOOKING_PHONE_COUNTRIES;
    return BOOKING_PHONE_COUNTRIES.filter(
      (c) =>
        countryName(c).toLowerCase().includes(q) ||
        c.toLowerCase().includes(q) ||
        getCountryCallingCode(c).includes(q),
    );
  }, [search]);

  function selectCountry(iso: CountryCode): void {
    const newDial = `+${getCountryCallingCode(iso)}`;
    const currentDial = `+${getCountryCallingCode(country)}`;
    const newValue = value.startsWith(currentDial)
      ? newDial + value.slice(currentDial.length)
      : value;
    setCountry(iso);
    onChange(newValue);
    setOpen(false);
    setTimeout(() => inputRef.current?.focus(), 30);
  }

  function handleInput(raw: string): void {
    const detected = detectCountryFromPhoneValue(raw);
    if (raw.trimStart().startsWith("+") && detected) setCountry(detected);
    onChange(formatPublicBookingPhoneValue(raw, detected ?? country));
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Escape") setOpen(false);
  }

  const isEmpty = isEmptyPublicBookingPhoneValue(value, dialCode);
  const isValid = required
    ? !isEmpty && isValidBookingPhone(value, country)
    : isEmpty || isValidBookingPhone(value, country);
  const showError = (touched || forceShowError) && !isValid;

  useEffect(() => {
    onValidChange?.(isValid);
  }, [isValid, onValidChange]);

  return (
    <div className={cn("relative", className)}>
      <div
        ref={fieldRef}
        className={cn(
          PUBLIC_BOOKING_LIGHT_TOKENS_CLASS,
          "flex h-9 items-stretch overflow-hidden rounded-lg border border-input bg-white text-[13px] text-foreground transition-colors focus-within:border-action-500/60 focus-within:ring-2 focus-within:ring-action-500/25",
          showError &&
            "border-destructive focus-within:border-destructive focus-within:ring-destructive/20",
        )}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "flex shrink-0 items-center gap-1 border-r border-input px-2.5 text-[13px] text-foreground transition-colors hover:bg-secondary/60",
            BOOKING_FOCUS_RING,
            open && "bg-secondary/60",
          )}
          aria-label={tI18n("landAuswaehlen")}
        >
          <span className="text-[18px] leading-none">{flagEmoji(country)}</span>
          <span className="text-primary/70">{dialCode}</span>
          <ChevronDown
            className={cn(
              "h-3 w-3 text-primary/40 transition-transform",
              open && "rotate-180",
            )}
          />
        </button>

        <input
          ref={inputRef}
          type="tel"
          value={getPublicBookingPhoneDisplayValue(value, dialCode)}
          onChange={(e) => handleInput(e.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={handleKeyDown}
          placeholder={tI18n("k151")}
          className="min-w-0 flex-1 bg-transparent px-3 outline-none placeholder:text-primary/35"
        />

        {value ? (
          <button
            type="button"
            onClick={() => {
              onChange("");
              setTouched(false);
            }}
            className="mr-1 flex items-center px-1 text-primary/30 hover:text-primary/60"
            aria-label={tI18n("eingabeLoeschen")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      {showError ? (
        <p className="mt-1 text-[11.5px] text-destructive">
          {tI18n("bitteGueltigeTelefonnummerEingeben")}
        </p>
      ) : null}

      {open && pos ? (
        <PublicBookingFloatingPanelPortal
          open={open}
          pos={pos}
          panelRef={panelRef}
        >
          <div className="flex shrink-0 items-center border-b border-border/60 bg-white px-3 py-2">
            <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-primary/40" />
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tI18n("landSuchen")}
              className="min-w-0 flex-1 bg-white text-[13px] text-foreground outline-none placeholder:text-primary/35"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="ml-1 text-primary/30 hover:text-primary/60"
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:thin]">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-[13px] text-primary/45">
                {tI18n("keineErgebnisse")}
              </p>
            ) : (
              filtered.map((iso) => (
                <button
                  key={iso}
                  type="button"
                  onClick={() => selectCountry(iso)}
                  className={cn(
                    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-foreground transition-colors hover:bg-secondary/70",
                    country === iso &&
                      "bg-action-50 font-medium text-action-700",
                  )}
                >
                  <span className="text-[16px] leading-none">
                    {flagEmoji(iso)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {countryName(iso)}
                  </span>
                  <span className="shrink-0 text-primary/45">
                    +{getCountryCallingCode(iso)}
                  </span>
                </button>
              ))
            )}
          </div>
        </PublicBookingFloatingPanelPortal>
      ) : null}
    </div>
  );
}
